import { Suspense, lazy, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { RouteObject } from 'react-router';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { BlogLayout } from './blog/BlogLayout';
import { BlogPost } from './blog/components/BlogPost';
import { PostsList } from './blog/components/PostsList';
import { posts } from './blog/services/posts';
import { CategoryView } from './blog/components/CategoryView';
import { TagView } from './blog/components/TagView';
import { MainLayout } from 'src/layouts/MainLayout';
import { initGA, logPageView } from '../utils/analytics';
import { SubscriptionSuccess } from './pages/Subscription/subscribingSuccess';
import { SupportSuccess } from './pages/Support/supportSuccess';
import { ProtectedRoute } from './protectedRoute';
import Disclaimer from 'src/content/pages/Legal/Disclaimer';
import Terms from 'src/content/pages/Legal/Terms';
import Privacy from 'src/content/pages/Legal/Privacy';

// Helper component to extract accountId from params and pass to AccountDashboard
const AccountDashboardWithParams = () => {
  const { accountId } = useParams();
  return <AccountDashboard accountId={accountId!} />;
}


const Loader = (Component) => (props) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages
const Home = Loader(lazy(() => import('src/content/home')));
const Overview = Loader(lazy(() => import('src/content/Dashboard').then(module => ({ default: module.Dashboard }))));
const SimulationRunner = Loader(lazy(() => import('src/content/SimulationRunner').then(module => ({ default: module.SimulationRunner }))));
const Settings = Loader(lazy(() => import('src/content/Settings').then(module => ({ default: module.Settings }))));
const AccountDashboard = Loader(lazy(() => import('src/content/AccountDashboard').then(module => ({ default: module.AccountDashboardShell }))));
const AdminSettings = Loader(lazy(() => import('src/pages/AdminSettings').then(module => ({ default: module.AdminSettings }))));

// Status
const Status404 = Loader(lazy(() => import('src/content/pages/Status/Status404')));
const Status500 = Loader(lazy(() => import('src/content/pages/Status/Status500')));


const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Home />
      },
      {
        path: 'home',
        element: <Navigate to="/" replace />
      }
    ]
  },
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'blog',
        element: <BlogLayout />,
        children: [
          {
            path: '',
            element: <PostsList posts={posts} />
          },
          {
            path: 'post/:slug',
            element: <BlogPost />
          },
          {
            path: 'category/:categoryId',
            element: <CategoryView />
          },
          {
            path: 'tag/:tagId',
            element: <TagView />
          }
        ]
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <Overview />
          </ProtectedRoute>
        )
      },
      {
        path: 'accounts/:accountId',
        element: (
          <ProtectedRoute>
            <AccountDashboardWithParams />
          </ProtectedRoute>
        )
      },
      {
        path: 'simulation-runner',
        element: (
          <ProtectedRoute>
            <SimulationRunner />
          </ProtectedRoute>
        )
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        )
      },
      {
        path: 'admin',
        element: (
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        )
      }
    ]
  },
  {
    path: '',
    element: <MainLayout />,
    children: [
      {
        path: 'disclaimer',
        element: <Disclaimer />
      },
      {
        path: 'terms',
        element: <Terms />
      },
      {
        path: 'privacy',
        element: <Privacy />
      },
      {
        path: 'subscription/success',
        element: (
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        )
      },
      {
        path: 'support/success',
        element: (
          <ProtectedRoute>
            <SupportSuccess />
          </ProtectedRoute>
        )
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  }
];


export const Router = () => {
  const location = useLocation()

  useEffect(() => {
    initGA('G-7TZ7HM2NFX')
  }, [])

  useEffect(() => {
    logPageView(location.pathname + location.search)
  }, [location])

  return routes
}
